/**
 * Admin → Cooldowns control panel.
 *
 * Single page that owns every "X-second cooldown" on the site.
 * Reads from `GET /admin/cooldowns`, writes individual changes via
 * `PUT /admin/cooldowns/{key}`. No JSON editing required.
 *
 * Each row exposes:
 *   • A "human" picker (minutes + seconds inputs) so admins don't
 *     have to do math to set "30 minutes"
 *   • A raw seconds field for power users
 *   • A "Save" button (only enabled when value differs from server)
 *   • A "Reset to default" link
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Timer, RotateCcw, Save, Check, UserX } from "lucide-react";
import { UserSearchInput } from "@/components/admin/UserSearchInput";

export function CooldownsEditor() {
  const toast = useToast();
  const [rows, setRows] = useState([]);   // [{key, label, group, default, value}]
  const [drafts, setDrafts] = useState({}); // {key: editedSeconds}
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cooldowns");
      setRows(data.cooldowns || []);
      // Reset drafts on reload so the inputs reflect the server values.
      const next = {};
      (data.cooldowns || []).forEach((c) => { next[c.key] = c.value; });
      setDrafts(next);
    } catch (e) {
      toast.error(apiError(e, "Couldn't load cooldowns"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Group rows by section for the UI.
  const grouped = useMemo(() => {
    const out = {};
    for (const r of rows) {
      (out[r.group] = out[r.group] || []).push(r);
    }
    return out;
  }, [rows]);

  const save = async (key) => {
    const seconds = Math.max(0, parseInt(drafts[key], 10) || 0);
    setSavingKey(key);
    try {
      const { data } = await api.put(`/admin/cooldowns/${key}`, { seconds });
      toast.success(
        data?.bot_keys_synced?.length
          ? `Updated ${key} — Discord (${data.bot_keys_synced.join(", ")}) synced`
          : `Updated ${key}`,
      );
      setRows((rs) => rs.map((r) => r.key === key
        ? { ...r, value: seconds, bot_out_of_sync: false,
            bot_keys: Object.fromEntries(Object.keys(r.bot_keys || {}).map((k) => [k, seconds])) }
        : r));
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSavingKey(null);
    }
  };

  const resetToDefault = (row) => {
    setDrafts((d) => ({ ...d, [row.key]: row.default }));
  };

  return (
    <div className="space-y-4 fade-up" data-testid="cooldowns-editor-tab">
      <div className="glass-card p-4">
        <div className="flex items-center gap-2">
          <Timer size={16} className="text-[var(--teal)]" />
          <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            Cooldowns
          </h3>
        </div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Every "X-second cooldown" on the site, controlled in one place. Values are seconds.
          Changes apply on the next action — no restart needed.
        </p>
      </div>

      {loading && <div className="glass-card p-6 text-sm text-[var(--muted)]">Loading…</div>}

      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} className="glass-card p-0 overflow-hidden">
          <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
            <span className="label !mb-0">{group}</span>
            <span className="text-[10px] text-[var(--muted)]">{items.length} cooldown{items.length === 1 ? "" : "s"}</span>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm" data-testid={`cooldowns-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <th className="px-4 py-2">Setting</th>
                <th className="px-4 py-2 w-44">Minutes</th>
                <th className="px-4 py-2 w-40">Seconds</th>
                <th className="px-4 py-2 w-44">Value</th>
                <th className="px-4 py-2 w-44"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const isCount = r.unit === "count";
                const draft = drafts[r.key] ?? r.value;
                const dirty = draft !== r.value;
                const totalSecs = Math.max(0, parseInt(draft, 10) || 0);
                const mm = Math.floor(totalSecs / 60);
                const ss = totalSecs % 60;
                return (
                  <tr key={r.key} className="border-t border-white/5" data-testid={`cooldown-row-${r.key}`}>
                    <td className="px-4 py-2">
                      <div className="text-sm">{r.label}</div>
                      <div className="text-[10px] font-mono text-[var(--muted)]">{r.key}</div>
                      {Object.keys(r.bot_keys || {}).length > 0 && (
                        <div
                          className={`text-[10px] mt-0.5 ${r.bot_out_of_sync ? "text-amber-300" : "text-[var(--teal)]"}`}
                          data-testid={`cooldown-botsync-${r.key}`}
                        >
                          {r.bot_out_of_sync
                            ? `Discord still on ${Object.entries(r.bot_keys).map(([k, v]) => `${k}=${v ?? "unset"}`).join(", ")} — save to sync`
                            : `Discord synced (${Object.keys(r.bot_keys).join(", ")})`}
                        </div>
                      )}
                    </td>
                    {isCount ? (
                      <td className="px-4 py-2 text-[11px] text-[var(--muted)] italic" colSpan={2}>
                        Not a duration — this is how many uses are allowed before the lockout starts.
                      </td>
                    ) : (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min="0"
                            max="43200"
                            className="input w-24"
                            value={mm}
                            onChange={(e) => {
                              const m = Math.max(0, parseInt(e.target.value, 10) || 0);
                              setDrafts((d) => ({ ...d, [r.key]: m * 60 + ss }));
                            }}
                            data-testid={`cooldown-mm-${r.key}`}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min="0"
                            max="59"
                            className="input w-24"
                            value={ss}
                            onChange={(e) => {
                              const s = Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0));
                              setDrafts((d) => ({ ...d, [r.key]: mm * 60 + s }));
                            }}
                            data-testid={`cooldown-ss-${r.key}`}
                          />
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min={isCount ? 1 : 0}
                        max={isCount ? 1000 : 2592000}
                        className="input w-28 font-mono"
                        value={draft}
                        onChange={(e) => {
                          const total = Math.max(0, parseInt(e.target.value, 10) || 0);
                          setDrafts((d) => ({ ...d, [r.key]: total }));
                        }}
                        data-testid={`cooldown-secs-${r.key}`}
                      />
                      {r.default !== r.value && (
                        <button
                          onClick={() => resetToDefault(r)}
                          className="text-[10px] text-[var(--muted)] hover:text-[var(--bone)] mt-1 ml-1 flex items-center gap-1"
                          title={`Reset to ${r.default}${isCount ? "" : "s"}`}
                          data-testid={`cooldown-reset-${r.key}`}
                        >
                          <RotateCcw size={9} /> default {r.default}{isCount ? "" : "s"}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => save(r.key)}
                        disabled={!dirty || savingKey === r.key}
                        className={`btn btn-sm ${dirty ? "" : "btn-secondary"}`}
                        data-testid={`cooldown-save-${r.key}`}
                      >
                        {savingKey === r.key ? "…" : dirty ? <><Save size={11} /> Save</> : <><Check size={11} /> Saved</>}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      ))}

      <ClearForUserSection rows={rows} />
    </div>
  );
}


/**
 * Admin tool to wipe a single cooldown for one user — handy when you want
 * to free a stuck player without rolling back the global cooldown value.
 */
function ClearForUserSection({ rows }) {
  const toast = useToast();
  const [userId, setUserId] = useState("");
  const [userLabel, setUserLabel] = useState("");
  const [cooldownKey, setCooldownKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    // Default to the first cooldown once rows load.
    if (!cooldownKey && rows.length > 0) setCooldownKey(rows[0].key);
  }, [rows, cooldownKey]);

  const submit = async () => {
    if (!userId || !cooldownKey) {
      toast.error("Pick a user and a cooldown first.");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/cooldowns/clear-user", {
        user_id: userId,
        key: cooldownKey,
      });
      setLastResult(data.cleared);
      toast.success(`Cleared ${cooldownKey} for ${userLabel || userId}`);
    } catch (e) {
      toast.error(apiError(e, "Failed to clear"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card p-4 mt-1" data-testid="cooldowns-clear-user">
      <div className="flex items-center gap-2 mb-2">
        <UserX size={14} className="text-[var(--teal)]" />
        <h3 className="text-base" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          Clear a cooldown for one user
        </h3>
      </div>
      <p className="text-xs text-[var(--muted)] mb-3">
        Wipes the chosen cooldown only for the selected user. Does not change the
        global duration. Useful when someone got stuck after a server hiccup or
        a feature beta.
      </p>
      <div className="grid sm:grid-cols-[1fr_240px_auto] gap-2">
        <UserSearchInput
          onPick={(row) => {
            setUserId(row.discord_id || row.user_id || "");
            setUserLabel(row.display_name || row.discord_username || row.player_name || "");
          }}
          placeholder="Type a Discord name or in-game name…"
          dataTestid="cooldown-clear-user-search"
        />
        <select
          value={cooldownKey}
          onChange={(e) => setCooldownKey(e.target.value)}
          className="input"
          data-testid="cooldown-clear-select"
        >
          {rows.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
        <button
          onClick={submit}
          disabled={busy || !userId || !cooldownKey}
          className="btn"
          data-testid="cooldown-clear-submit"
        >
          {busy ? "Clearing…" : "Clear cooldown"}
        </button>
      </div>
      {lastResult && (
        <div className="text-[11px] text-[var(--muted)] mt-3 font-mono">
          {Object.entries(lastResult).map(([k, v]) => (
            <span key={k} className="inline-block mr-3">{k}: <span className="text-[var(--bone)]">{String(v)}</span></span>
          ))}
        </div>
      )}
    </div>
  );
}
